
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { InkPinsGrid } from "./InkPinsGrid";
import { WebToonsGrid } from "./WebToonsGrid";
import { WebNovelsGrid } from "./WebNovelsGrid";
import { PostGrid } from "./PostGrid";
import { CollectionsGrid } from "./CollectionsGrid";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

export const ContentTabs = () => {
  const [activeTab, setActiveTab] = useState("ink-pins");
  const isMobile = useIsMobile();
  
  const tabs = [
    { id: "ink-pins", label: "Pins" },
    { id: "webtoons", label: "Webtoons" },
    { id: "webnovels", label: "WebNovels" },
    { id: "posts", label: "Posts" },
    { id: "collections", label: "Coleções" },
  ];

  // Effect to hide sidebar on webtoons tab when on mobile
  useEffect(() => {
    const sidebar = document.querySelector("[data-sidebar]");
    if (sidebar && isMobile) {
      if (activeTab === "webtoons") {
        sidebar.classList.add("hidden");
      } else {
        sidebar.classList.remove("hidden");
      }
    }
    
    // Cleanup
    return () => {
      if (sidebar && isMobile) {
        sidebar.classList.remove("hidden");
      }
    };
  }, [activeTab, isMobile]);

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <motion.div 
        className="flex justify-center border-b border-border"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex space-x-4 md:space-x-8">
          {tabs.map((tab, index) => (
            <motion.button
              key={tab.id}
              className={cn(
                "px-2 py-3 text-xs sm:text-sm font-medium transition-all relative",
                activeTab === tab.id 
                  ? "text-primary" 
                  : "text-foreground/60 hover:text-foreground"
              )}
              onClick={() => setActiveTab(tab.id)}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {tab.label}
              {activeTab === tab.id && (
                <motion.span 
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" 
                  layoutId="activeTab"
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </motion.button>
          ))}
        </div>
      </motion.div>
      
      {/* Tab Content */}
      <div className="mt-4">
        <AnimatePresence mode="wait">
          {activeTab === "ink-pins" && (
            <motion.div
              key="ink-pins"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <InkPinsGrid />
            </motion.div>
          )}
          {activeTab === "webtoons" && (
            <motion.div
              key="webtoons"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <WebToonsGrid />
            </motion.div>
          )}
          {activeTab === "webnovels" && (
            <motion.div
              key="webnovels"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <WebNovelsGrid />
            </motion.div>
          )}
          {activeTab === "posts" && (
            <motion.div
              key="posts"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <PostGrid />
            </motion.div>
          )}
          {activeTab === "collections" && (
            <motion.div
              key="collections"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <CollectionsGrid />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
